import type { BluffPhase, OptionKind } from '../domain/bluff'

export const BLUFF_PACK_FORMAT = 'bar-bluff-pack'
export const BLUFF_PACK_VERSION = 1

export interface BluffRulesView { bluffSeconds: number | null; voteSeconds: number | null; truthPoints: number; foolPoints: number }

/** One voting option as players and the Big Screen see it before the reveal: no kind, no authors. */
export interface PublicOption { id: string; text: string }

/** An option after the reveal, when the truth, authors and votes may be shown to everyone. */
export interface RevealedOption extends PublicOption {
  kind: OptionKind
  authors: string[]
  voters: string[]
  points: number
}

export interface BluffReveal {
  truth: string
  explanation: string
  sourceName: string
  sourceUrl: string | null
  options: RevealedOption[]
}

/** The bluff part of `/api/player/session`. Only the player's own team's bluff, vote and authored options are included. */
export interface PlayerBluffView {
  phase: BluffPhase
  rules: BluffRulesView
  questionNumber: number
  questionCount: number
  isCaptain: boolean
  eligible: boolean
  myBluff: { text: string; status: 'ACCEPTED' | 'REJECTED' } | null
  options: Array<PublicOption & { mine: boolean }>
  myVoteOptionId: string | null
  votingClosed: boolean
  reveal: BluffReveal | null
  pointsEarned: number | null
}

export interface ScreenBluffView {
  phase: BluffPhase
  questionNumber: number
  questionCount: number
  submitted: number
  voted: number
  eligible: number
  options: PublicOption[]
  votingClosed: boolean
  reveal: BluffReveal | null
}

/** The host console sees everything, including the truth, from the moment a question opens. */
export interface HostBluffView {
  phase: BluffPhase
  rules: BluffRulesView
  packName: string
  questionNumber: number
  questionCount: number
  truth: { truth: string; aliases: string[]; explanation: string; sourceName: string; sourceUrl: string | null; preparedDecoys: string[] } | null
  submissions: Array<{ id: string; teamId: string; teamName: string; text: string; status: 'ACCEPTED' | 'REJECTED'; groupKey: string; submittedAt: string; nearTruth: boolean }>
  groups: Array<{ key: string; label: string; teams: string[]; submissionIds: string[] }>
  suggestions: Array<{ sourceKey: string; targetKey: string; sourceLabel: string; targetLabel: string }>
  decoys: Array<{ id: string; text: string; source: string }>
  optionCountPreview: number
  options: Array<PublicOption & { kind: OptionKind; authors: string[]; votes: number }>
  submitted: number
  voted: number
  eligible: number
  votingClosed: boolean
  missingTeams: string[]
  reveal: BluffReveal | null
}
